/** Pure derivations for the Shared-with-me page — no React, unit-tested.
 *
 * D32: the page lists cross-org resources only. The server decides membership
 * (`via === "external"`); these helpers only place each external row — nested
 * under a granted space when its stamp matches one, standalone otherwise.
 */

interface SharedModelLike {
  space_id?: string | null;
  via?: string;
  scheduled_deletion_date?: number;
}

const live = <T extends SharedModelLike>(m: T): boolean =>
  !m.scheduled_deletion_date;

/** The rows this page is about: live models from orgs the caller is not a
 *  member of. Everything else has a home on the Overview. */
export const selectExternalModels = <T extends SharedModelLike>(
  models: T[],
): T[] => models.filter((m) => live(m) && m.via === "external");

export interface SharedPartition<T> {
  /** External rows nested under a granted space, keyed by space_id. A granted
   *  space with no visible rows still renders — the grant is the entry, its
   *  models may simply not have loaded into the poll yet. */
  bySpace: Map<string, T[]>;
  /** External rows reached by a direct model share — no granted space claims
   *  their stamp. */
  standalone: T[];
}

export const partitionSharedModels = <T extends SharedModelLike>(
  externalModels: T[],
  grantedSpaceIds: ReadonlySet<string>,
): SharedPartition<T> => {
  const bySpace = new Map<string, T[]>();
  const standalone: T[] = [];
  for (const model of externalModels) {
    const spaceId = model.space_id ?? undefined;
    if (spaceId && grantedSpaceIds.has(spaceId)) {
      const bucket = bySpace.get(spaceId) ?? [];
      bucket.push(model);
      bySpace.set(spaceId, bucket);
    } else {
      standalone.push(model);
    }
  }
  return { bySpace, standalone };
};

/** What the nav item counts: containers plus standalone shares. Nested models
 *  ride their space rather than inflating the number. */
export const sharedWithMeCount = (
  spaceCount: number,
  standaloneCount: number,
): number => spaceCount + standaloneCount;

/** "viewer" → "Viewer" — the wire carries FGA relation spellings. */
export const roleLabel = (role: string): string =>
  role.length > 0 ? role.charAt(0).toUpperCase() + role.slice(1) : role;
